import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { results as resultsCache } from "../local/db";

// #644 — placings/composite for ONE class. Same visibility-aware
// fetch-then-poll shape as ../hooks/useRideStatuses.js (which wraps
// common-fe's useRideStatuses), but keyed on a single classId rather than a
// set of ride ids, and reading GET /class/:classId/results — no new route.
//
// Two modes, chosen by the caller via `intervalMs`:
//   - omitted/falsy -> fetch once (for a class that's already wrapped; its
//     results won't change again once every ride is FINAL).
//   - set -> poll at that interval, pausing while the tab is hidden and doing
//     an immediate refresh on return, exactly like the ride-status poll.
// docs/score/announcer.md's freshness table: poll ONLY the current/live class.
export const RESULTS_POLL_MS = 15000;

export function useClassResults(
  classId,
  { intervalMs, enabled = true, online = true } = {},
) {
  const scoreApi = useScoreApi();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const classIdRef = useRef(classId);
  classIdRef.current = classId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;
  const onlineRef = useRef(online);
  onlineRef.current = online;

  const active = Boolean(enabled) && Boolean(classId);

  // #919 — ClassResults.jsx now flips a POLLING class into fetch-once mode
  // the moment it learns (from a poll response) that the class is final,
  // instead of only ever starting a not-yet-live class in fetch-once mode.
  // Without this guard, that mode flip (intervalMs number -> falsy) re-runs
  // the effect below and its fetch-once branch calls refresh() again — an
  // extra, redundant request the instant any class crosses into final.
  // Only skip the fetch when we already have a result for THIS classId;
  // reset the moment classId/active changes so a genuinely new fetch-once
  // class still gets its own initial fetch.
  const hasResultsForIdRef = useRef(false);
  useEffect(() => {
    hasResultsForIdRef.current = false;
  }, [classId, active]);

  // #705/#912 — read-through offline cache. Always fetch, render, write the
  // result array VERBATIM into IndexedDB. A failed fetch falls back to the
  // last-cached array so placings/composites keep showing instead of vanishing
  // (genuine offline is caught here, not pre-gated on connectivity). We cache
  // exactly what score-api returned, so a PARTIAL row still has no number and
  // the #697 reveal gate (keyed on each cached row's status) holds.
  const refresh = useCallback(async () => {
    const id = classIdRef.current;
    if (!id) return;
    setLoading(true);
    try {
      const rows = unwrap(await apiRef.current.results.getClassResults(id)) || [];
      setResults(rows);
      setError(null);
      await resultsCache.put(id, rows);
    } catch (err) {
      const cached = await resultsCache.get(id);
      if (cached) {
        setResults(cached.rows);
        setError(null);
      } else {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (results !== null) hasResultsForIdRef.current = true;
  }, [results]);

  useEffect(() => {
    if (!active) {
      setResults(null);
      return undefined;
    }

    // Fetch-once mode: no interval to arm, just get the latest and stop.
    // #919 — but not if a poll already delivered a result for this class (see
    // hasResultsForIdRef above) — that data is already fresh, so re-fetching
    // here would just be a redundant extra request the instant a live class
    // crosses into final and this effect re-runs on the intervalMs flip.
    if (!intervalMs) {
      if (!hasResultsForIdRef.current) refresh();
      return undefined;
    }

    // Polling mode: never fetch (not even the initial call) while the tab is
    // hidden. The visibilitychange handler below refreshes immediately the
    // moment it comes back, so nothing is stale on return.
    if (document.visibilityState !== "hidden") refresh();

    let timer = null;
    const start = () => {
      if (document.visibilityState === "hidden") return;
      if (timer == null) timer = setInterval(refresh, intervalMs);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else {
        refresh();
        start();
      }
    };
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // classId (not just active) drives re-subscription so switching the live
    // class re-fetches immediately rather than waiting out the old interval.
    // `online` is kept as a dep purely as a reconnect trigger — refresh() now
    // always attempts the network (falling back to cache on failure), so a
    // connectivity flip still re-fetches a fetch-once class on reconnect.
  }, [classId, active, intervalMs, refresh, online]);

  return { results, loading, error, refresh };
}

export default useClassResults;
